import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Area,
  BarChart as RechartsBarChart, Cell
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCompact = (value) => {
  if (!value && value !== 0) return "-";
  if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toFixed(0);
};

const formatFull = (value) => {
  return new Intl.NumberFormat("en-IN").format(Math.round(value || 0));
};

const PacingTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#111] border border-[#333] rounded-lg p-3 text-sm shadow-xl">
      <p className="font-semibold text-[#E4E4E7] mb-2 border-b border-[#333] pb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex justify-between gap-6 py-0.5">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="font-mono text-white">{formatFull(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

const DailyBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  return (
    <div className="bg-[#111] border border-[#333] rounded-lg p-3 text-sm shadow-xl">
      <p className="font-semibold text-[#E4E4E7] mb-2 border-b border-[#333] pb-1">{label}</p>
      <p className="flex justify-between gap-6 py-0.5">
        <span className="text-[#FFEB00]">Net Sales</span>
        <span className="font-mono text-white">{formatFull(data?.net_sales)}</span>
      </p>
      <p className="flex justify-between gap-6 py-0.5">
        <span className="text-[#A1A1AA]">Orders</span>
        <span className="font-mono text-white">{data?.order_count || 0}</span>
      </p>
    </div>
  );
};

// Color gradient for bar chart based on value magnitude
const getBarColor = (value, max) => {
  if (!max || max === 0) return "#FFEB00";
  const ratio = value / max;
  if (ratio > 0.8) return "#00E676";
  if (ratio > 0.6) return "#FFEB00";
  if (ratio > 0.4) return "#FF9100";
  if (ratio > 0.2) return "#FF6D00";
  return "#FF3D00";
};

const DailyRevenueCharts = ({ clientId, month, monthLabel }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = clientId
      ? `${API}/revenue/${clientId}/${month}/daily`
      : `${API}/dashboard/daily-revenue?month=${month}`;
    axios.get(url)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clientId, month]);

  if (loading) return <div className="p-4 text-[#71717A]">Loading daily data...</div>;

  const dailyData = data?.daily_data || [];

  if (!dailyData.length) {
    return (
      <div className="card-flat p-6" data-testid="daily-revenue-empty">
        <h3 className="text-lg font-semibold mb-2">Day-wise Revenue Pacing</h3>
        <p className="text-[#71717A] text-sm">No daily data available yet. Sync revenue to populate day-wise tracking.</p>
      </div>
    );
  }

  const expectedDaily = data?.expected_daily || 0;
  const daysInMonth = data?.days_in_month || 30;

  // Build chart data: format dates, compute cumulative for client-level if not pre-computed
  let cumulative = 0;
  const chartData = dailyData.map((d, i) => {
    const dayNum = parseInt(d.date.split("-")[2], 10);
    const label = `${dayNum}`;
    cumulative += d.net_sales;
    return {
      ...d,
      day: label,
      fullDate: d.date,
      cumulative_actual: d.cumulative_actual ?? Math.round(cumulative),
      cumulative_expected: d.cumulative_expected ?? Math.round(expectedDaily * (i + 1)),
    };
  });

  const maxDailyNet = Math.max(...chartData.map(d => d.net_sales), 1);
  const totalActual = chartData[chartData.length - 1]?.cumulative_actual || 0;
  const totalExpected = data?.expected_monthly || 0;
  const pacingPercent = totalExpected > 0 ? Math.round((totalActual / totalExpected) * 100) : 0;
  const isAhead = totalActual >= (chartData[chartData.length - 1]?.cumulative_expected || 0);

  return (
    <div className="space-y-4" data-testid="daily-revenue-charts">
      {/* Pacing Summary Bar */}
      <div className="card-flat p-4 border-l-4" style={{ borderLeftColor: isAhead ? "#00E676" : "#FF6D00" }}>
        <div className="flex items-center justify-between">
          <div>
            <span className="small-label">Revenue Pacing</span>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-2xl font-bold font-mono" style={{ color: isAhead ? "#00E676" : "#FF6D00" }}>
                {pacingPercent}%
              </span>
              <span className="text-sm text-[#71717A]">
                {isAhead ? "Ahead of pace" : "Behind pace"} | {chartData.length} of {daysInMonth} days
              </span>
            </div>
          </div>
          <div className="text-right">
            <span className="small-label">Actual vs Expected</span>
            <p className="text-sm font-mono mt-1">
              <span className="text-[#FFEB00]">{formatCompact(totalActual)}</span>
              <span className="text-[#52525B] mx-1">/</span>
              <span className="text-[#00E676]">{formatCompact(totalExpected)}</span>
            </p>
          </div>
        </div>
        {/* Mini progress bar */}
        <div className="mt-3 h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(pacingPercent, 100)}%`,
              background: isAhead
                ? "linear-gradient(90deg, #00E676, #69F0AE)"
                : "linear-gradient(90deg, #FF6D00, #FFAB40)"
            }}
          />
        </div>
      </div>

      {/* Cumulative Pacing Line Chart */}
      <div className="card-flat p-6" data-testid="pacing-line-chart">
        <h3 className="text-lg font-semibold mb-1">Cumulative Revenue Pacing</h3>
        <p className="text-xs text-[#71717A] mb-4">Actual cumulative revenue vs linear expected pace</p>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <defs>
              <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FFEB00" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#FFEB00" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="expectedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00E676" stopOpacity={0.12} />
                <stop offset="95%" stopColor="#00E676" stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis
              dataKey="day"
              stroke="#52525B"
              fontSize={11}
              tick={{ fill: "#71717A" }}
            />
            <YAxis
              stroke="#52525B"
              fontSize={11}
              tickFormatter={formatCompact}
              tick={{ fill: "#71717A" }}
            />
            <Tooltip content={<PacingTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: 12, color: "#A1A1AA" }}
            />
            <Area
              type="monotone"
              dataKey="cumulative_expected"
              name="Expected Pace"
              stroke="#00E676"
              fill="url(#expectedGradient)"
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="cumulative_actual"
              name="Actual Revenue"
              stroke="#FFEB00"
              fill="url(#actualGradient)"
              strokeWidth={2.5}
              dot={{ fill: "#FFEB00", r: 2.5, strokeWidth: 0 }}
              activeDot={{ fill: "#FFEB00", r: 5, stroke: "#000", strokeWidth: 2 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Daily Revenue Bar Chart */}
      <div className="card-flat p-6" data-testid="daily-bar-chart">
        <h3 className="text-lg font-semibold mb-1">Daily Revenue</h3>
        <p className="text-xs text-[#71717A] mb-4">Individual day net sales — color intensity shows magnitude</p>
        <ResponsiveContainer width="100%" height={260}>
          <RechartsBarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
            <XAxis
              dataKey="day"
              stroke="#52525B"
              fontSize={11}
              tick={{ fill: "#71717A" }}
            />
            <YAxis
              stroke="#52525B"
              fontSize={11}
              tickFormatter={formatCompact}
              tick={{ fill: "#71717A" }}
            />
            <Tooltip content={<DailyBarTooltip />} />
            <Bar dataKey="net_sales" name="Net Sales" radius={[4, 4, 0, 0]} maxBarSize={28}>
              {chartData.map((entry, idx) => (
                <Cell key={idx} fill={getBarColor(entry.net_sales, maxDailyNet)} />
              ))}
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DailyRevenueCharts;
